import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, Clock, Calendar, Image, BookOpen, Video, Bell, MapPin, Star, MessageCircle, Target } from 'lucide-react';
import { useMascot } from '@/contexts/mascot-context';

interface HaasOrientationProps {
  onComplete?: () => void;
}

const STORAGE_KEY = 'user_seen_orientation';

export default function HaasOrientation({ onComplete }: HaasOrientationProps) {
  const [step, setStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const { haasEnabled } = useMascot();

  useEffect(() => {
    if (!haasEnabled) {
      if (onComplete) onComplete();
      return;
    }
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      setIsVisible(true);
    } else if (onComplete) {
      onComplete();
    }
  }, [onComplete, haasEnabled]);

  const handleNext = () => {
    if (step < 1) {
      setStep(1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="flex justify-end p-3 pb-0">
          <button
            onClick={handleComplete}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 0 && (
          <div className="px-6 pb-6">
            <div className="flex flex-col items-center gap-4 mb-6">
              <img
                src="/haas/haas-main.png"
                alt="Haas the service dog"
                className="w-36 h-36 object-contain flex-shrink-0 drop-shadow-lg"
              />
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to ShowMe</h2>
                <p className="text-gray-600 text-[15px] leading-relaxed">
                  ShowMe helps caregivers create visual supports for individuals with special needs.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5 mb-5">
              <ToolItem icon={<Clock className="w-4 h-4" />} label="Visual Timers" />
              <ToolItem icon={<Calendar className="w-4 h-4" />} label="Picture Schedules" />
              <ToolItem icon={<Image className="w-4 h-4" />} label="Choice Boards" />
              <ToolItem icon={<BookOpen className="w-4 h-4" />} label="Social Narratives" />
              <ToolItem icon={<Video className="w-4 h-4" />} label="Video Models" />
              <ToolItem icon={<Bell className="w-4 h-4" />} label="Reminders" />
              <ToolItem icon={<MapPin className="w-4 h-4" />} label="Location Prep" />
              <ToolItem icon={<Star className="w-4 h-4" />} label="Reward Charts" />
              <ToolItem icon={<MessageCircle className="w-4 h-4" />} label="Messenger" />
              <ToolItem icon={<Target className="w-4 h-4" />} label="Progress Tracking" />
            </div>

            <p className="text-xs text-gray-500 italic mb-6 text-center">
              Meet Haas, our mascot, inspired by a real service dog.
            </p>

            <div className="flex items-center justify-between">
              <StepDots current={0} total={2} />
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleComplete}>
                  Skip
                </Button>
                <Button size="sm" onClick={handleNext}>
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="px-6 pb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">How ShowMe Works</h2>
            <p className="text-gray-600 text-[15px] mb-5">
              Everything is organized inside a Hub — one hub per individual you support.
            </p>

            <div className="relative rounded-2xl p-6 mb-5 overflow-hidden" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 50%, #fdf2f8 100%)' }}>
              <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" viewBox="0 0 400 300">
                <path d="M200,60 Q120,120 100,180" fill="none" stroke="#818cf8" strokeWidth="2" strokeDasharray="6,4" />
                <path d="M200,60 Q280,120 300,180" fill="none" stroke="#818cf8" strokeWidth="2" strokeDasharray="6,4" />
                <path d="M200,60 Q200,120 200,180" fill="none" stroke="#818cf8" strokeWidth="2" strokeDasharray="6,4" />
              </svg>

              <div className="relative flex flex-col items-center gap-6">
                <div className="flex items-center gap-3">
                  <img
                    src="/haas/haas-guide.png"
                    alt="Haas"
                    className="w-20 h-20 object-contain drop-shadow-md"
                  />
                  <div className="bg-indigo-600 text-white px-5 py-2.5 rounded-2xl font-bold text-base shadow-lg shadow-indigo-200">
                    Your Hub
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 w-full max-w-sm">
                  <DiagramBubble
                    color="bg-orange-50 border-orange-200"
                    iconColor="text-orange-600"
                    label="Resources"
                    items={["Timers", "Schedules", "Boards", "Narratives"]}
                    emoji="📋"
                  />
                  <DiagramBubble
                    color="bg-pink-50 border-pink-200"
                    iconColor="text-pink-600"
                    label="Goals"
                    items={["AI Plans", "Progress", "Rewards"]}
                    emoji="🎯"
                  />
                  <DiagramBubble
                    color="bg-violet-50 border-violet-200"
                    iconColor="text-violet-600"
                    label="Connect"
                    items={["Messenger", "Village", "Sharing"]}
                    emoji="💬"
                  />
                </div>
              </div>
            </div>

            <p className="text-gray-600 text-[15px] mb-6">
              Create a hub, add your tools, and invite collaborators — all in one place.
            </p>

            <div className="flex items-center justify-between">
              <StepDots current={1} total={2} />
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleComplete}>
                  Skip
                </Button>
                <Button size="sm" onClick={handleComplete}>
                  Get Started <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
      <span className="text-primary">{icon}</span>
      {label}
    </div>
  );
}

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all ${
            i === current ? 'w-6 bg-primary' : 'w-2 bg-gray-300'
          }`}
        />
      ))}
    </div>
  );
}

function DiagramBubble({ color, iconColor, label, items, emoji }: {
  color: string;
  iconColor: string;
  label: string;
  items: string[];
  emoji: string;
}) {
  return (
    <div className={`flex flex-col items-center rounded-2xl border ${color} p-3 shadow-sm`}>
      <span className="text-xl mb-1">{emoji}</span>
      <span className={`text-xs font-bold ${iconColor} mb-1.5`}>{label}</span>
      <div className="text-[10px] text-gray-500 text-center leading-relaxed">
        {items.map((item, i) => (
          <div key={i}>{item}</div>
        ))}
      </div>
    </div>
  );
}
